import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Input, Pagination, Divider, Spin } from 'antd';
import { WithLatestStateDeviceNode } from '../RootTree';
import { LoadingOutlined } from '@ant-design/icons';
import { useListAllDevicesByPage } from '../../../../hooks/socket/cameras';
import { beeXLogger } from '../../../../helpers/logger';

export const AllCameras = ({
  cameras,
  currentPage,
  pageSize,
  onPageChange,
  isLoading,
  getIsCameraPinned,
  onPinCamera,
  total,
  selectedSerial,
  onSelectCamera,
  namespace,
  onSearch,
}) => {
  const timeRequestRef = useRef(Date.now());
  const {
    isLoading: isSearching,
    data: searchResult,
    send,
  } = useListAllDevicesByPage();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const searchNameRef = useRef('');

  const fetchCameras = useCallback(
    ({ page, name }) => {
      let data1 = {
        namespace,
        deviceType: 'camera',
        timeRequest: timeRequestRef.current,
        page,
        size: 15,
        tag: 'live-list',
        name,
      };
      send(data1);
    },
    [namespace, send],
  );

  const handleSearch = (value) => {
    if (!value) {
      setIsSearchActive(false);
      searchNameRef.current = '';
      return;
    }

    searchNameRef.current = value;

    setIsSearchActive(true);
    timeRequestRef.current = Date.now();
    fetchCameras({ page: 0, name: value });
  };

  const finalCurrentPage = useMemo(() => {
    if (!isSearchActive) {
      return currentPage;
    }

    return searchPage;
  }, [isSearchActive, searchPage, currentPage]);

  const finalTotal = useMemo(() => {
    if (!isSearchActive) {
      return total;
    }
    return searchResult?.total || 0;
  }, [isSearchActive, searchResult, total]);

  const handlePageChange = (page, pageSize) => {
    if (!isSearchActive) {
      onPageChange(page, pageSize);
    } else {
      setSearchPage(page);
      fetchCameras({ page: page - 1, name: searchNameRef.current });
    }
  };

  const finalPageSize = useMemo(() => {
    if (!isSearchActive) {
      return pageSize;
    }

    return 15;
  }, [isSearchActive, pageSize]);

  const camerasToRender = useMemo(() => {
    if (!isSearchActive) {
      return cameras;
    }
    // onSearch(searchResult?.devices || []);
    return searchResult?.devices || [];
  }, [isSearchActive, searchResult, cameras]);

  useEffect(() => {
    if (searchNameRef.current === '') {
      return onSearch(null);
    }
    if (camerasToRender) {
      onSearch(camerasToRender);
    }
  }, [camerasToRender, onSearch]);

  return (
    <>
      <Input.Search
        placeholder="Search camera"
        onSearch={handleSearch}
        loading={isLoading}
        allowClear
      />
      <Divider className="camera-list-divider" />
      <div className="camera-list">
        {(isLoading || isSearching) && (
          <div className="camera-list-loading">
            <Spin indicator={<LoadingOutlined />} />
          </div>
        )}
        <div className="camera-list-items">
          {!isLoading && !isSearching && (
            <React.Fragment>
              {camerasToRender?.map((cam) => (
                <WithLatestStateDeviceNode
                  key={cam.serial}
                  name={cam.name}
                  serial={cam.serial}
                  state={cam.state}
                  isPinned={getIsCameraPinned?.(cam.serial) || false}
                  onPin={(pinned) => onPinCamera(cam, pinned)}
                  isSelected={selectedSerial === cam.serial}
                  onSelect={() => onSelectCamera(cam)}
                />
              ))}
            </React.Fragment>
          )}
        </div>

        <div className="camera-list-pagination">
          <Pagination
            size="small"
            showLessItems
            showSizeChanger={false}
            total={finalTotal}
            current={finalCurrentPage}
            pageSize={finalPageSize}
            onChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
};
